// Slim full-width sticky CTA bar pinned to the bottom of every page.
// Shows 5s after page load; dismissing hides it for 24 hours.
// Hides while the lead magnet popup is open; its CTA force-opens the popup.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  LEAD_MAGNET_OPEN_EVENT,
  LEAD_MAGNET_CLOSE_EVENT,
  LEAD_MAGNET_FORCE_OPEN_EVENT,
} from "@/components/LeadMagnetPopup";

const NAVY = "#1B2F5E";
const GOLD = "#E8952A";
const BAR_DISMISS_KEY = "stickyLeadBarDismissed";
const BAR_HIDE_MS = 24 * 60 * 60 * 1000; // 24 hours
const SHOW_AFTER_MS = 5_000;

export default function StickyLeadBar() {
  const [show, setShow] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    let dismissedRecently = false;
    try {
      const ts = localStorage.getItem(BAR_DISMISS_KEY);
      dismissedRecently = !!ts && Date.now() - parseInt(ts, 10) < BAR_HIDE_MS;
    } catch { /* SSR / privacy mode — silently no-op */ }
    if (dismissedRecently) return;
    const t = setTimeout(() => setShow(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  // Hide while the lead magnet popup is open.
  useEffect(() => {
    const onOpen = () => setPopupOpen(true);
    const onClose = () => setPopupOpen(false);
    window.addEventListener(LEAD_MAGNET_OPEN_EVENT, onOpen);
    window.addEventListener(LEAD_MAGNET_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(LEAD_MAGNET_OPEN_EVENT, onOpen);
      window.removeEventListener(LEAD_MAGNET_CLOSE_EVENT, onClose);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(BAR_DISMISS_KEY, Date.now().toString()); } catch { /* noop */ }
    setShow(false);
  };

  const openPopup = () => {
    window.dispatchEvent(new CustomEvent(LEAD_MAGNET_FORCE_OPEN_EVENT));
  };

  if (!show || popupOpen) return null;

  return (
    <>
      <style>{`@keyframes stickyLeadBarSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      <div
        data-testid="sticky-lead-bar"
        role="complementary"
        aria-label="Free guide offer"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9000,
          background: NAVY, minHeight: 56,
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 12, padding: "8px 44px 8px 16px",
          boxShadow: "0 -6px 24px rgba(27, 47, 94, 0.4)",
          animation: "stickyLeadBarSlideUp 300ms ease",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >

      <span
        style={{
          color: "#ffffff", fontSize: 14, lineHeight: 1.35,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          maxWidth: "60vw",
        }}
      >
        📚 Get our free guide: <strong>Beat Information Overload</strong>
      </span>

      <button
        type="button"
        onClick={openPopup}
        data-testid="sticky-lead-bar-cta"
        style={{
          background: GOLD, color: "#ffffff", border: "none", cursor: "pointer",
          fontWeight: 700, fontSize: 13, padding: "9px 20px", borderRadius: 999,
          whiteSpace: "nowrap", flexShrink: 0,
          transition: "transform 150ms ease, background-color 150ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.05)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        Get It Free
      </button>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss banner"
        data-testid="sticky-lead-bar-dismiss"
        style={{
          position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)",
          width: 28, height: 28, border: "none", background: "transparent",
          color: "rgba(255,255,255,0.75)", cursor: "pointer", borderRadius: "50%",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <X size={15} />
      </button>
      </div>
    </>
  );
}
